import { useEffect, useMemo } from 'react';
import { PRODUCT_DISCOVERY_PROJECT } from '@atlassian/jira-common-constants/src/project-types.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { useProjectContext } from '@atlassian/jira-providers-project-context/src/index.tsx';
import { useAccountId } from '@atlassian/jira-tenant-context-controller/src/components/account-id/index.tsx';
import { BANNER_NAME } from '../constants.tsx';
import {
	useJpdPremiumOnboardingBannerState,
	useJpdPremiumOnboardingBannerActions,
} from './store/index.tsx';
import { useHasPolarisInPath } from './helpers.tsx';

export const useBannerJpdPremiumOnboarding = () => {
	const hasPolarisInPath = useHasPolarisInPath();
	const { data: project } = useProjectContext();
	const accountId = useAccountId();
	const { isDismissed, height, shouldShow, isLoading } = useJpdPremiumOnboardingBannerState();
	const { fetchBannerVisibility } = useJpdPremiumOnboardingBannerActions();
	const isDiscoveryProject = project?.projectType === PRODUCT_DISCOVERY_PROJECT;
	const isInJpdBoundary = useMemo(
		() => hasPolarisInPath || isDiscoveryProject,
		[isDiscoveryProject, hasPolarisInPath],
	);

	useEffect(() => {
		if (
			!accountId ||
			!fg('polaris_pol-15006_premium_onboarding_banner') ||
			!fg('polaris_eap_customers')
		) {
			return;
		}

		fetchBannerVisibility(accountId);
	}, [accountId, fetchBannerVisibility]);

	// use `polaris_eap_customers` to check if the customer is in the EAP
	// use `polaris_pol-15006_premium_onboarding_banner` as a kill switch
	// If I'll use `polaris_eap_customers` only, we won't have a quick way to disable the banner
	return useMemo(
		() => ({
			isEnabled:
				// eslint-disable-next-line jira/ff/no-preconditioning
				isInJpdBoundary &&
				shouldShow &&
				!isDismissed &&
				!isLoading &&
				fg('polaris_eap_customers') &&
				fg('polaris_pol-15006_premium_onboarding_banner'),
			name: BANNER_NAME,
			height,
		}),
		[isInJpdBoundary, shouldShow, isDismissed, height, isLoading],
	);
};
